import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { logos } from '../../assets/images';

const Signin = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showVerificationBanner, setShowVerificationBanner] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const { mergeCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if redirected from email verification
    if (location.state?.emailVerified || location.state?.verificationError) {
      setShowVerificationBanner(true);
      // Set the custom message from the verification
      if (location.state?.message) {
        setCustomMessage(location.state.message);
      }
      // Clear the state so banner doesn't show on refresh
      window.history.replaceState({}, document.title);
    } else if (location.state?.message) {
      // Check if there's a custom message (e.g., from blog creation)
      setCustomMessage(location.state.message);
    }
  }, [location]);

  // Clear error when credentials change
  useEffect(() => {
    if (error) {
      setError('');
      setErrorType('');
    }
  }, [credentials.email, credentials.password]);

  // If user is already authenticated, show a message instead of login form
  if (isAuthenticated) {
    return (
      <div className="login-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', minHeight: '0' }}>
        <div className="container" style={{
          maxWidth: '380px',
          width: '100%',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '12px' }}>
            <img
              src={logos.UBLong}
              alt="UnsupervisedBias"
              style={{
                maxWidth: '280px',
                width: '100%',
                height: 'auto',
                marginBottom: '6px'
              }}
            />
            <p style={{ fontSize: '16px', margin: '0', fontWeight: '400', opacity: 0.9 }}>You're Already Signed In</p>
          </div>

          <div style={{
            backgroundColor: '#e3f2fd',
            color: '#1565c0',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 8px 0' }}>You are currently signed in as:</p>
            <p style={{ margin: '0', fontWeight: 'bold' }}>{user?.email}</p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              onClick={() => navigate('/profile')}
              style={{
                padding: '12px 28px',
                fontSize: '17px',
                border: 'none',
                outline: 'none',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, var(--pink) 0%, var(--violet) 100%)',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(216, 134, 255, 0.4)',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(216, 134, 255, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(216, 134, 255, 0.4)';
              }}
            >
              Go to Profile
            </button>

            <button
              onClick={() => navigate('/')}
              style={{
                padding: '12px 28px',
                fontSize: '17px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                outline: 'none',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(credentials, keepLoggedIn);
    if (result.success) {
      // Merge guest cart with user cart after successful login
      await mergeCart();
      // Redirect to the page they were trying to access, or default to profile
      const redirectTo = location.state?.from || '/profile';
      navigate(redirectTo);
    } else {
      // Set error message and type from backend
      setError(result.error);
      setErrorType(result.errorType || '');
    }
  };

  return (
    <div className="login-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', minHeight: '0' }}>
      <div className="container" style={{
        maxWidth: '380px',
        width: '100%',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <img
            src={logos.UBLong}
            alt="UnsupervisedBias"
            style={{
              maxWidth: '280px',
              width: '100%',
              height: 'auto',
              marginBottom: '6px'
            }}
          />
          <p style={{ fontSize: '16px', margin: '0', fontWeight: '400', opacity: 0.9 }}>Sign in to your account</p>
        </div>

        {showVerificationBanner && !location.state?.verificationError && (
          <div style={{
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '12px',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>{customMessage || '✓ Email verified successfully! You can now sign in.'}</span>
            <button
              onClick={() => {
                setShowVerificationBanner(false);
                setCustomMessage('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#2e7d32',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0 5px',
                minWidth: '24px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {showVerificationBanner && location.state?.verificationError && (
          <div style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '12px',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>{customMessage || 'Email verification failed. Please try again.'}</span>
            <button
              onClick={() => {
                setShowVerificationBanner(false);
                setCustomMessage('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#c62828',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0 5px',
                minWidth: '24px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {customMessage && !showVerificationBanner && (
          <div style={{
            backgroundColor: '#e3f2fd',
            color: '#1565c0',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '12px',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>{customMessage}</span>
            <button
              onClick={() => setCustomMessage('')}
              style={{
                background: 'none',
                border: 'none',
                color: '#1565c0',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0 5px',
                minWidth: '24px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {error && (
          <div className="error" style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '10px',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: errorType === 'email_not_found' ? '10px' : '0' }}>
              {error}
            </div>
            {errorType === 'email_not_found' && (
              <button
                onClick={() => navigate('/create-account', {
                  state: {
                    fromLogin: true,
                    email: credentials.email
                  }
                })}
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  border: 'none',
                  outline: 'none',
                  borderRadius: '6px',
                  background: 'linear-gradient(135deg, var(--pink) 0%, var(--violet) 100%)',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 2px 10px rgba(216, 134, 255, 0.4)',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 15px rgba(216, 134, 255, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 10px rgba(216, 134, 255, 0.4)';
                }}
              >
                Create Account
              </button>
            )}
          </div>
        )}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <input
            type="text"
            placeholder="Email"
            value={credentials.email}
            onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
            required
            style={{
              padding: '12px',
              fontSize: '16px',
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}
          />
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              required
              style={{
                padding: '12px 45px 12px 12px',
                fontSize: '16px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '5px',
                display: 'flex',
                alignItems: 'center',
                color: '#666'
              }}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              )}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px' }}>
            <input
              type="checkbox"
              id="keepLoggedIn"
              checked={keepLoggedIn}
              onChange={(e) => setKeepLoggedIn(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: 'var(--pink)'
              }}
            />
            <label
              htmlFor="keepLoggedIn"
              style={{
                fontSize: '14px',
                color: '#ccc',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Keep me logged in
            </label>
          </div>

          <button
            type="submit"
            style={{
              padding: '12px 28px',
              fontSize: '17px',
              border: 'none',
              outline: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, var(--pink) 0%, var(--violet) 100%)',
              color: '#fff',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(216, 134, 255, 0.4)',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
              width: '100%',
              marginTop: '5px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(216, 134, 255, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(216, 134, 255, 0.4)';
            }}
          >
            Sign in
          </button>

          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <Link
              to="/forgot-password"
              state={{ email: credentials.email }}
              style={{
                color: 'var(--pink)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Forgot Password?
            </Link>
          </div>
        </form>

        <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '14px', color: '#ccc' }}>
          New to UB?{' '}
          <Link
            to="/create-account"
            style={{
              color: 'var(--pink)',
              textDecoration: 'none',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.target.style.textDecoration = 'none';
            }}
          >
            Join now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signin;
